import { Monitor, Smartphone } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import type { BaseballTrendAspectRatio } from "@shared/schemas/api";

interface BaseballTrendAspectRatioPickerProps {
  value: BaseballTrendAspectRatio;
  onChange: (next: BaseballTrendAspectRatio) => void;
}

/** Step 4 picker — choose the output aspect ratio. The chosen ratio is used
 *  for BOTH the broadcast preview pair AND the final Kling render. Pulled
 *  out of the GenerationBar (footer) so the user can't accidentally skip
 *  it before generating previews. */
export function BaseballTrendAspectRatioPicker({
  value,
  onChange,
}: BaseballTrendAspectRatioPickerProps) {
  const { t } = useLanguage();

  return (
    <div className="space-y-4 md:space-y-5">
      <div>
        <h3 className="text-base md:text-lg font-semibold text-foreground">
          {t.baseballTrend.aspectRatioTitle}
        </h3>
        <p className="text-xs md:text-sm text-muted-foreground mt-0.5">
          {t.baseballTrend.aspectRatioHint}
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <RatioCard
          icon={Monitor}
          label={t.baseballTrend.aspectRatioLandscape}
          sublabel="16:9"
          recommended={t.baseballTrend.actionRecommendedBadge}
          isActive={value === "16:9"}
          onClick={() => onChange("16:9")}
          previewClass="aspect-video"
        />
        <RatioCard
          icon={Smartphone}
          label={t.baseballTrend.aspectRatioPortrait}
          sublabel="9:16"
          isActive={value === "9:16"}
          onClick={() => onChange("9:16")}
          previewClass="aspect-[9/16] max-h-16"
        />
      </div>
    </div>
  );
}

function RatioCard({
  icon: Icon,
  label,
  sublabel,
  recommended,
  isActive,
  onClick,
  previewClass,
}: {
  icon: typeof Monitor;
  label: string;
  sublabel: string;
  recommended?: string;
  isActive: boolean;
  onClick: () => void;
  previewClass: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={isActive}
      className={cn(
        "group relative flex items-center gap-3 p-4 md:p-5 rounded-2xl border text-start transition-all duration-200",
        isActive
          ? "bg-gradient-to-br from-primary/15 to-primary/5 border-primary shadow-md ring-2 ring-primary/30"
          : "bg-card border-border hover:border-primary/40 hover:shadow-sm md:hover:-translate-y-0.5",
      )}
    >
      {recommended && (
        <span className="absolute top-2 end-2 px-1.5 py-0.5 rounded-md bg-emerald-500/15 text-emerald-600 text-[10px] font-bold uppercase tracking-wider ring-1 ring-emerald-500/30">
          {recommended}
        </span>
      )}
      <div
        className={cn(
          "flex items-center justify-center shrink-0 rounded-xl bg-muted",
          previewClass,
          isActive ? "ring-2 ring-primary/50" : "ring-1 ring-border",
          // Width controls the small thumbnail width — bigger for landscape
          previewClass.includes("video") ? "w-20" : "w-9",
        )}
      >
        <Icon
          className={cn(
            "w-4 h-4",
            isActive ? "text-primary" : "text-muted-foreground",
          )}
          strokeWidth={2.25}
        />
      </div>
      <div className="flex flex-col">
        <span className="text-sm md:text-base font-semibold text-foreground">
          {label}
        </span>
        <span className="text-xs md:text-sm text-muted-foreground">
          {sublabel}
        </span>
      </div>
    </button>
  );
}
